import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Spinner,
  Switch,
} from '@fluentui/react-components';

import { useAppStore } from '@/stores/chatbotStore';
import apiClient from '@/services/api';
import { Capability } from '@/types/api';

interface ChatbotSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The chatbot's tool switches. They apply to everyone who uses this chatbot. */
const ChatbotSettingsDialog: React.FC<ChatbotSettingsDialogProps> = ({ open, onOpenChange }) => {
  const { currentChatbot, updateChatbot } = useAppStore();
  const [capabilities, setCapabilities] = useState<Capability[] | null>(null);
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setValues({ ...(currentChatbot?.tools ?? {}) });
    apiClient
      .getCapabilities()
      .then(setCapabilities)
      .catch(() => setError('Impossible de charger les réglages.'));
  }, [open, currentChatbot]);

  const save = async () => {
    if (!currentChatbot || !capabilities) return;
    setSaving(true);
    setError(null);
    try {
      // A tool the instance switches off is not the chatbot's choice: it is left as stored
      const tools = Object.fromEntries(
        capabilities.filter((c) => c.available).map((c) => [c.key, values[c.key] !== false])
      );
      await updateChatbot(currentChatbot.id, { tools });
      onOpenChange(false);
    } catch {
      setError("L'enregistrement a échoué. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogTitle>Réglages du chatbot</DialogTitle>
        <DialogContent>
          <DialogBody>
            <p className="settings-intro">
              Outils de « {currentChatbot?.name} ».
            </p>
            {!capabilities && !error && <Spinner size="small" />}
            {capabilities && (
              <div className="settings-list">
                {capabilities.map((c) => (
                  <Switch
                    key={c.key}
                    checked={c.available && values[c.key] !== false}
                    disabled={!c.available || saving}
                    onChange={(_, data) => setValues({ ...values, [c.key]: data.checked })}
                    label={
                      <span className="settings-label">
                        <strong>{c.label}</strong>
                        <span>{c.available ? c.description : 'Désactivé sur cette instance.'}</span>
                      </span>
                    }
                  />
                ))}
              </div>
            )}
            {error && <p className="settings-error">{error}</p>}
          </DialogBody>
        </DialogContent>
        <DialogActions>
          <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button appearance="primary" onClick={save} disabled={saving || !capabilities}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default ChatbotSettingsDialog;
